<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">Accreditations</h4>
      <el-button type="primary" size="small" @click="showCreateDialog">Create</el-button>
    </div>

    <card>
      <el-input v-model="search" placeholder="Search accreditations..." clearable class="mb-3" style="max-width:400px" />

      <el-table :data="filteredList" v-loading="loading" style="width: 100%">
        <el-table-column label="ID" prop="id" width="80" />
        <el-table-column label="Logo" width="80">
          <template slot-scope="{ row }">
            <img v-if="row.logo" :src="row.logo" style="max-height:30px;max-width:60px" />
          </template>
        </el-table-column>
        <el-table-column label="Abbreviation" prop="abbreviation" width="140" />
        <el-table-column label="Name EN" min-width="200">
          <template slot-scope="{ row }">
            {{ row.name && row.name.en || '' }}
          </template>
        </el-table-column>
        <el-table-column label="Name RU" min-width="200">
          <template slot-scope="{ row }">
            {{ row.name && row.name.ru || '' }}
          </template>
        </el-table-column>
        <el-table-column label="Country" prop="country_code" width="90" />
        <el-table-column label="Website" width="80">
          <template slot-scope="{ row }">
            <a v-if="row.web_site_link" :href="row.web_site_link" target="_blank" class="small">Link</a>
          </template>
        </el-table-column>
        <el-table-column fixed="right" width="80">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="editItem(row)">Edit</el-button>
          </template>
        </el-table-column>
      </el-table>
    </card>

    <!-- Create Dialog -->
    <el-dialog title="Create Accreditation" :visible.sync="dialogVisible" width="700px">
      <div class="row">
        <div class="col-md-4">
          <label>Abbreviation *</label>
          <el-input v-model="form.abbreviation" placeholder="e.g. JCI" class="mb-3" />

          <label>Country Code</label>
          <el-input v-model="form.country_code" placeholder="e.g. US" maxlength="2" class="mb-3" />

          <label>Website</label>
          <el-input v-model="form.web_site_link" placeholder="https://..." class="mb-3" />

          <label>Logo</label>
          <div class="accreditation-logo mb-3">
            <img v-if="logoPreview" :src="logoPreview" class="accreditation-logo__img" />
            <div>
              <button type="button" class="accreditation-logo__btn" @click.prevent="$refs.logoInput.click()">
                Upload Logo
              </button>
              <input type="file" ref="logoInput" accept="image/jpeg,image/png" @change="handleLogoUpload" style="display:none" />
            </div>
            <small v-if="logoFile" class="text-success d-block mt-1">{{ logoFile.name }}</small>
          </div>
        </div>
        <div class="col-md-8">
          <el-tabs v-model="activeLang">
            <el-tab-pane v-for="lang in langs" :key="lang" :label="lang.toUpperCase()" :name="lang">
              <label>Name {{ lang.toUpperCase() }}</label>
              <el-input v-model="form.name[lang]" :placeholder="`Name in ${lang.toUpperCase()}`" class="mb-3" />

              <label>Description {{ lang.toUpperCase() }}</label>
              <el-input type="textarea" :rows="6" v-model="form.description[lang]" :placeholder="`Description in ${lang.toUpperCase()}`" />
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="saving" @click="saveItem">Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";

function emptyLangs(langs = []) {
  return langs.reduce((acc, l) => { acc[l] = ""; return acc; }, {});
}

export default {
  data() {
    return {
      loading: false,
      saving: false,
      search: "",
      activeLang: "en",
      dialogVisible: false,
      logoFile: null,
      logoDataUrl: null,
      form: {
        id: 0,
        name: {},
        description: {},
        abbreviation: "",
        web_site_link: "",
        logo: "",
        country_code: "",
      },
    };
  },
  computed: {
    ...mapState("specialities", ["accreditations"]),
    ...mapState("cmsApiCatalogs", ["domains"]),
    langs() {
      return (this.domains || []).map(({ language }) => language).filter(Boolean);
    },
    logoPreview() {
      return this.logoDataUrl || this.form.logo || "";
    },
    filteredList() {
      if (!this.search) return this.accreditations;
      const q = this.search.toLowerCase();
      return this.accreditations.filter(a => {
        const nameEn = (a.name && a.name.en || "").toLowerCase();
        const nameRu = (a.name && a.name.ru || "").toLowerCase();
        const abbr = (a.abbreviation || "").toLowerCase();
        return nameEn.includes(q) || nameRu.includes(q) || abbr.includes(q);
      });
    },
  },
  methods: {
    ...mapActions("specialities", ["getAccreditations", "upsertAccreditation"]),
    ...mapActions("cmsApiCatalogs", ["getDomains"]),
    resetForm() {
      this.form = {
        id: 0,
        name: emptyLangs(this.langs),
        description: emptyLangs(this.langs),
        abbreviation: "",
        web_site_link: "",
        logo: "",
        country_code: "",
      };
      this.logoFile = null;
      this.logoDataUrl = null;
      this.activeLang = this.langs[0] || "en";
    },
    showCreateDialog() {
      this.resetForm();
      this.dialogVisible = true;
    },
    editItem(row) {
      this.$router.push({ name: "Accreditation", params: { id: row.id } });
    },
    handleLogoUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (!["image/jpeg", "image/png"].includes(file.type)) {
        return alert("Only .jpg, .jpeg, .png files are allowed");
      }
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        this.logoDataUrl = reader.result;
        this.logoFile = file;
      });
      reader.readAsDataURL(file);
    },
    async saveItem() {
      if (!this.form.abbreviation) {
        return this.$notify({ type: "warning", title: "Abbreviation is required" });
      }
      if (!this.form.name.en) {
        return this.$notify({ type: "warning", title: "Name EN is required" });
      }
      this.saving = true;
      try {
        await this.upsertAccreditation({
          data: {
            id: this.form.id,
            name: this.form.name,
            description: this.form.description,
            abbreviation: this.form.abbreviation,
            web_site_link: this.form.web_site_link,
            country_code: this.form.country_code,
            logo: this.form.logo,
          },
          logoFile: this.logoFile,
        });
        this.$notify({ type: "success", title: "Accreditation saved" });
        this.dialogVisible = false;
        await this.getAccreditations();
      } catch (e) {
        this.$notify({ type: "warning", title: "Error saving accreditation" });
        console.error(e);
      } finally {
        this.saving = false;
      }
    },
  },
  async created() {
    this.loading = true;
    await Promise.all([
      this.getDomains(),
      this.getAccreditations(),
    ]).finally(() => { this.loading = false; });
    this.resetForm();
  },
};
</script>

<style scoped>
.accreditation-logo__img {
  display: block;
  max-width: 150px;
  max-height: 80px;
  margin-bottom: 10px;
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 4px;
  background: #fff;
  object-fit: contain;
}
.accreditation-logo__btn {
  padding: 6px 16px;
  background: #f4f8fb;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
}
.accreditation-logo__btn:hover {
  background: #e9ecef;
}
</style>
